import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import { safeParseNumber } from '@morpho-org/blue-sdk-viem';
import { parseApiAsset, ZERO } from '@morpho-org/dapp-core';
import { useMemo } from 'react';
import { getOraclePriceFeedDecimals } from '@/helpers/oracle';
import { usePaginatedOffset } from '@/hooks/usePaginatedOffset';
import type { MarketBorrowersTableRow } from '../config';
import type { MarketBorrowerItem } from '../config/queryKey';
import { toTableToken } from '../../tableToken';
export interface UseMarketBorrowersRowProps {
  items: readonly MarketBorrowerItem[];
  chainId: ChainId;
  marketId: string;
  currentPage: number;
}
const useMarketBorrowersRow = t0 => {
  const $ = _c(11);
  const {
    items,
    chainId,
    marketId,
    currentPage
  } = t0;
  const {
    paginatedOffset,
    initOffset,
    setNextOffset
  } = usePaginatedOffset(ZERO);
  let t1;
  bb0: {
    const first = items[0];
    if (!first) {
      let t2;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [];
        $[0] = t2;
      } else {
        t2 = $[0];
      }
      t1 = t2;
      break bb0;
    }
    initOffset(currentPage);
    let t2;
    if ($[1] !== chainId || $[2] !== currentPage || $[3] !== first.collateralAsset || $[4] !== first.lltv || $[5] !== first.loanAsset || $[6] !== items || $[7] !== marketId || $[8] !== paginatedOffset || $[9] !== setNextOffset) {
      const loanAsset = parseApiAsset(toTableToken(first.loanAsset, items.map(_temp), chainId));
      const collateralAsset = first.collateralAsset ? parseApiAsset(toTableToken(first.collateralAsset, items.map(_temp2), chainId)) : null;
      const decimals = getOraclePriceFeedDecimals({
        loanTokenDecimals: first.loanAsset.decimals,
        collateralTokenDecimals: first.collateralAsset?.decimals ?? 18
      });
      const lltv = safeParseNumber(first.lltv);
      const largest = items.reduce(_temp3);
      const marketBorrowState = {
        borrowAssets: BigInt(largest.borrowAssets),
        borrowShares: BigInt(largest.borrowShares),
        price: null
      };
      t2 = items.map(item_2 => {
        const borrowShareValue = item_2.borrowShare === null ? ZERO : safeParseNumber(item_2.borrowShare);
        const currentOffset = paginatedOffset.current;
        setNextOffset(borrowShareValue, currentPage);
        const borrowAssets = BigInt(item_2.borrowAssets);
        const borrowShares = BigInt(item_2.borrowShares);
        const collateral = {
          amount: BigInt(item_2.collateralAssets),
          amountUsd: item_2.collateralAssetsUsd === null ? null : safeParseNumber(item_2.collateralAssetsUsd),
          asset: collateralAsset
        };
        return {
          borrower: {
            address: item_2.userAddress,
            chainId
          },
          collateral,
          loan: {
            amount: borrowAssets,
            amountUsd: item_2.borrowAssetsUsd === null ? null : safeParseNumber(item_2.borrowAssetsUsd),
            asset: loanAsset
          },
          health: {
            healthFactor: item_2.healthFactor,
            lltv,
            ltv: item_2.ltv === null ? null : safeParseNumber(item_2.ltv),
            decimals,
            borrowShares,
            market: {
              marketId,
              loanAsset: {
                symbol: first.loanAsset.symbol ?? ""
              },
              collateralAsset: first.collateralAsset ? {
                symbol: first.collateralAsset.symbol ?? ""
              } : null,
              state: marketBorrowState
            },
            collateral
          },
          borrowShare: {
            value: borrowShareValue,
            percentageOffset: currentOffset
          }
        };
      });
      $[1] = chainId;
      $[2] = currentPage;
      $[3] = first.collateralAsset;
      $[4] = first.lltv;
      $[5] = first.loanAsset;
      $[6] = items;
      $[7] = marketId;
      $[8] = paginatedOffset;
      $[9] = setNextOffset;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    t1 = t2;
  }
  return t1;
};
export default useMarketBorrowersRow;
function _temp(item) {
  return {
    amount: item.borrowAssets,
    amountUsd: item.borrowAssetsUsd
  };
}
function _temp2(item_0) {
  return {
    amount: item_0.collateralAssets,
    amountUsd: item_0.collateralAssetsUsd
  };
}
function _temp3(max, item_1) {
  return BigInt(item_1.borrowShares) > BigInt(max.borrowShares) ? item_1 : max;
}